// src/components/NewAppointmentForm.tsx
// Formulario actualizado con CORRECCIÓN DE FECHA en modal de éxito

import React, { useState, useEffect, useRef } from 'react';
import { displayPrice } from '../utils/priceDisplay';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService, Service, AvailableSlot } from '../services/api';
import { CajaMotivo, motivoDelDia, pedirDiasDelServicio, MotivosDias } from './MotivoDelDia';
import { Stethoscope, Sparkles, XCircle, Zap, CalendarDays, MapPin, Target, AlertTriangle, MessageCircle, User, ClipboardList, FileText, CheckCircle, Clock } from 'lucide-react';
import './PublicProfessionalLanding.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Custom Calendar Component (mantener igual)
interface CustomCalendarProps {
  selectedDate: string;
  onDateSelect: (date: string) => void;
  professionalSchedule: any[];
  blockedDates?: string[];
  specialDayDates?: string[];
  onlySpecialDays?: boolean;
  minDate?: string;
  maxDate?: string;
  /** Días habilitados para el servicio elegido (contempla su horario propio,
   *  que puede abrir días fuera del horario general). null = usar el general. */
  serviceAvailableDates?: string[] | null;
  /** Por qué no hay turnos cada día (lo calcula el backend para el panel). */
  motivos?: MotivosDias;
}

const CustomCalendar: React.FC<CustomCalendarProps> = ({
  selectedDate,
  onDateSelect,
  professionalSchedule,
  blockedDates = [],
  specialDayDates = [],
  onlySpecialDays = false,
  minDate,
  maxDate,
  serviceAvailableDates = null,
  motivos = {}
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  // El día gris que tocaron, para mostrar por qué no tiene turnos
  const [motivoVisible, setMotivoVisible] = useState<{ fecha: string; texto: string } | null>(null);

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const isDateAvailable = (date: Date): boolean => {
    const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    if (blockedDates.includes(dateStr)) return false;
    if (specialDayDates.includes(dateStr)) return true;
    if (onlySpecialDays) return false;

    // Con servicio elegido mandan los días que calculó el backend (ya resuelve
    // el horario propio del servicio, que puede habilitar días no laborales).
    if (serviceAvailableDates !== null) {
      return serviceAvailableDates.includes(dateStr);
    }

    return esDiaLaboral(date);
  };

  // ¿Ese día de la semana se atiende según el horario general?
  const esDiaLaboral = (date: Date): boolean => {
    const dayOfWeek = date.getDay();
    const daySchedule = professionalSchedule.find(day => {
      const dayMapping = {
        'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
        'thursday': 4, 'friday': 5, 'saturday': 6
      };
      return dayMapping[day.day as keyof typeof dayMapping] === dayOfWeek;
    });

    return daySchedule ? daySchedule.isWorking : false;
  };

  const isDateBlocked = (date: Date): boolean => {
    const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    return blockedDates.includes(dateStr);
  };

  const isPastDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const toLocalDateStr = (d: Date): string => {
    return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
  };

  const isInRange = (date: Date): boolean => {
    const dateStr = toLocalDateStr(date);
    const min = minDate || toLocalDateStr(new Date());
    const maxD = maxDate || (() => { const d = new Date(); d.setDate(d.getDate() + 30); return toLocalDateStr(d); })();

    return dateStr >= min && dateStr <= maxD;
  };

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
   
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
   
    const days = [];
    const currentDate = new Date(startDate);
   
    for (let i = 0; i < 42; i++) {
      const date = new Date(currentDate);
      const isCurrentMonth = date.getMonth() === month;
      const isAvailable = isCurrentMonth && isDateAvailable(date) && !isPastDate(date) && isInRange(date);
      const isPast = isPastDate(date);
      const isBlocked = isCurrentMonth && !isPast && isDateBlocked(date);
      const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
      const isSelected = dateStr === selectedDate;
      const isToday = date.toDateString() === new Date().toDateString();
      const motivo = isCurrentMonth && !isAvailable && !isPast
        ? motivoDelDia({ fecha: dateStr, diaSemana: date.getDay(), motivos, maxDate, bloqueada: blockedDates.includes(dateStr), soloEspeciales: onlySpecialDays, diaLaboral: esDiaLaboral(date) })
        : null;

      days.push({
        date: new Date(date),
        dateStr,
        day: date.getDate(),
        isCurrentMonth,
        isAvailable,
        isPast,
        isBlocked,
        isSelected,
        isToday,
        motivo
      });
     
      currentDate.setDate(currentDate.getDate() + 1);
    }
   
    return days;
  };

  const days = generateCalendarDays();

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDayClick = (dayInfo: any) => {
    if (dayInfo.isAvailable) {
      setMotivoVisible(null);
      const d = dayInfo.date;
      onDateSelect(`${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`);
    } else if (dayInfo.motivo) {
      setMotivoVisible({ fecha: dayInfo.dateStr, texto: dayInfo.motivo });
    }
  };

  return (
    <div className="calendar-premium rounded-2xl p-4">
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={previousMonth}
          className="p-2 hover:bg-gray-100 rounded-xl transition-all duration-200"
        >
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <h3 className="text-base font-bold text-gray-800 tracking-wide">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>

        <button
          type="button"
          onClick={nextMonth}
          className="p-2 hover:bg-gray-100 rounded-xl transition-all duration-200"
        >
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map(day => (
          <div key={day} className="text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider py-2">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((dayInfo, index) => {
          let dayClasses = "w-9 h-9 flex items-center justify-center text-sm rounded-xl cursor-pointer transition-all duration-200 ";

          if (!dayInfo.isCurrentMonth) {
            dayClasses += "text-gray-300 cursor-not-allowed ";
          } else if (dayInfo.isPast) {
            dayClasses += "text-gray-400 cursor-not-allowed bg-gray-50/50 ";
          } else if (dayInfo.isBlocked) {
            dayClasses += "text-red-400 bg-red-50 cursor-help line-through font-medium ";
          } else if (dayInfo.isAvailable) {
            if (dayInfo.isSelected) {
              dayClasses += "bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-semibold shadow-lg shadow-blue-500/25 scale-105 ";
            } else {
              dayClasses += "bg-emerald-100 text-emerald-800 hover:bg-emerald-200 hover:scale-105 font-semibold ";
            }
          } else {
            dayClasses += "text-red-400 bg-red-50 cursor-help hover:bg-red-100 ";
          }

          if (motivoVisible && motivoVisible.fecha === dayInfo.dateStr) {
            dayClasses += "ring-2 ring-amber-400 ring-offset-1 ";
          } else if (dayInfo.isToday && !dayInfo.isSelected) {
            dayClasses += "ring-2 ring-indigo-300 ring-offset-1 ";
          }

          return (
            <button
              key={index}
              type="button"
              onClick={() => handleDayClick(dayInfo)}
              disabled={!dayInfo.isCurrentMonth || (!dayInfo.isAvailable && !dayInfo.motivo)}
              aria-disabled={!dayInfo.isAvailable || undefined}
              title={dayInfo.motivo || undefined}
              className={dayClasses}
            >
              {dayInfo.day}
            </button>
          );
        })}
      </div>
      {motivoVisible && (
        <CajaMotivo fecha={motivoVisible.fecha} texto={motivoVisible.texto} onCerrar={() => setMotivoVisible(null)} />
      )}

      <div className="mt-4 flex items-center justify-center space-x-4 text-xs flex-wrap gap-y-1">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-emerald-100 border border-emerald-300 rounded mr-1"></div>
          <span className="text-gray-500">Disponible</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-100 border border-red-300 rounded mr-1"></div>
          <span className="text-gray-500">No disponible</span>
        </div>
        {blockedDates.length > 0 && (
          <div className="flex items-center">
            <div className="w-3 h-3 bg-red-50 border border-red-200 rounded mr-1"></div>
            <span className="text-gray-500">Bloqueado</span>
          </div>
        )}
        <div className="flex items-center">
          <div className="w-3 h-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded mr-1"></div>
          <span className="text-gray-500">Seleccionado</span>
        </div>
      </div>
      {!motivoVisible && (
        <p className="mt-2 text-center text-[11px] text-gray-400">Tocá un día no disponible para ver por qué.</p>
      )}
    </div>
  );
};

interface User {
  id: number;
  nombre: string;
  apellido: string;
  es_profesional: boolean;
  professional_type: 'salud' | 'belleza';
  especialidad: string;
}

interface LocationOption {
  id: number;
  name: string;
  address?: string;
  city?: string;
  phone?: string;
}

interface AppointmentForm {
  serviceId: string;
  serviceIds: number[];
  locationId: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  appointmentDate: string;
  appointmentTime: string;
  notes: string;
  isFirstTime: boolean;
  urgencyLevel: 'low' | 'normal' | 'high';
}

const NewAppointmentForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Sobreturno: leer query params
  const preloadDate = searchParams.get('date') || '';
  const preloadTime = searchParams.get('time') || '';
  const isOverride = searchParams.get('override') === 'true';
  
  // Estados para datos del backend
  const [services, setServices] = useState<Service[]>([]);
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  // Días habilitados para el servicio elegido (respeta su horario propio)
  const [serviceAvailableDates, setServiceAvailableDates] = useState<string[] | null>(null);
  const [motivosDias, setMotivosDias] = useState<MotivosDias>({});
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Estados para sedes
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Estados para el calendario mejorado
  const [professionalSchedule, setProfessionalSchedule] = useState<any[]>([]);
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [specialDayDates, setSpecialDayDates] = useState<string[]>([]);
  const [onlySpecialDays, setOnlySpecialDays] = useState(false);
  const [maxAdvanceBooking, setMaxAdvanceBooking] = useState<number>(30);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);

  // Estados para el modal de éxito
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [appointmentCreated, setAppointmentCreated] = useState<any>(null);

  // Estados para campos dinámicos del booking
  const [bookingExtraFields, setBookingExtraFields] = useState<any>(null);
  const [patientExtraData, setPatientExtraData] = useState<Record<string, string>>({});

  // 🆕 Comprobante de pago (se sube al crear; queda auto-verificado)
  const [comprobanteFile, setComprobanteFile] = useState<File | null>(null);
  const [uploadingComprobante, setUploadingComprobante] = useState(false);

  // 🆕 Autocompletado de pacientes anteriores del profesional
  const [patientSuggestions, setPatientSuggestions] = useState<any[]>([]);
  const [showPatientSuggestions, setShowPatientSuggestions] = useState(false);
  const skipPatientSearchRef = useRef(false); // evita re-buscar tras seleccionar

  const [formData, setFormData] = useState<AppointmentForm>({
    serviceId: '',
    serviceIds: [],
    locationId: '',
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    appointmentDate: preloadDate,
    appointmentTime: preloadTime,
    notes: '',
    isFirstTime: false,
    urgencyLevel: 'normal'
  });

  // Verificar usuario y cargar servicios
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const loginStatus = localStorage.getItem('isLoggedIn');
    
    if (!userData || loginStatus !== 'true') {
      navigate('/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      
      if (!parsedUser.es_profesional) {
        navigate('/');
        return;
      }
      
      setUser(parsedUser);
      loadServices(parsedUser.id);
      loadLocations(parsedUser.id);
      loadBookingExtraFields(parsedUser.id);
    } catch (error) {
      console.error('Error parsing user data:', error);
      navigate('/login');
    }
  }, [navigate]);

  // Cargar sedes del profesional
  const loadLocations = async (professionalId: number) => {
    try {
      setLoadingLocations(true);
      const res = await fetch(`${API_URL}/api/locations/professional/${professionalId}/public`);
      const data = await res.json();
      if (data.success && data.locations) {
        setLocations(data.locations);
        if (data.locations.length === 1) {
          // Si hay una sola sede, seleccionarla automáticamente
          const loc = data.locations[0];
          setFormData(prev => ({ ...prev, locationId: String(loc.id) }));
          loadProfessionalSchedule(professionalId, loc.id);
        } else if (data.locations.length > 1) {
          // Múltiples sedes
          if (preloadDate && preloadTime) {
            // Sobreturno/precarga: auto-seleccionar la sede default
            const defaultLoc = data.locations.find((l: any) => l.is_default) || data.locations[0];
            setFormData(prev => ({ ...prev, locationId: String(defaultLoc.id) }));
            loadProfessionalSchedule(professionalId, defaultLoc.id);
          } else {
            // Normal: esperar selección del usuario
            setFormData(prev => ({ ...prev, locationId: '' }));
          }
        } else {
          // Sin sedes: cargar schedule sin locationId
          loadProfessionalSchedule(professionalId);
        }
      } else {
        setLocations([]);
        loadProfessionalSchedule(professionalId);
      }
    } catch (err) {
      console.error('Error cargando sedes:', err);
      setLocations([]);
      loadProfessionalSchedule(professionalId);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Cargar configuración de campos extra del booking
  const loadBookingExtraFields = async (professionalId: number) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/professionals/${professionalId}/booking-extra-fields`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.booking_extra_fields?.enabled && data.booking_extra_fields.fields?.length > 0) {
        setBookingExtraFields(data.booking_extra_fields);
      }
    } catch (err) {
      console.error('Error cargando campos extra:', err);
    }
  };

  // Cargar horarios del profesional para el calendario
  const loadProfessionalSchedule = async (professionalId: number, locationId?: number) => {
    try {
      setLoadingSchedule(true);
      const locParam = locationId ? `?locationId=${locationId}` : '';
      const response = await fetch(`${API_URL}/api/schedule/professional/${professionalId}/config/public${locParam}`);

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.scheduleConfig && data.scheduleConfig.workingDays) {
          setProfessionalSchedule(data.scheduleConfig.workingDays);
          if (data.scheduleConfig.maxAdvanceBooking) {
            setMaxAdvanceBooking(data.scheduleConfig.maxAdvanceBooking);
          }
          setOnlySpecialDays(data.scheduleConfig.onlySpecialDays || false);
          console.log('Horarios del profesional cargados:', data.scheduleConfig.workingDays);
        }
      } else {
        console.log('No se pudieron cargar horarios, usando configuración por defecto');
        setProfessionalSchedule([
          { day: 'sunday', dayName: 'Domingo', isWorking: false },
          { day: 'monday', dayName: 'Lunes', isWorking: true },
          { day: 'tuesday', dayName: 'Martes', isWorking: true },
          { day: 'wednesday', dayName: 'Miércoles', isWorking: true },
          { day: 'thursday', dayName: 'Jueves', isWorking: true },
          { day: 'friday', dayName: 'Viernes', isWorking: true },
          { day: 'saturday', dayName: 'Sábado', isWorking: false }
        ]);
      }
    } catch (error) {
      console.error('Error cargando horarios:', error);
      setProfessionalSchedule([
        { day: 'sunday', dayName: 'Domingo', isWorking: false },
        { day: 'monday', dayName: 'Lunes', isWorking: true },
        { day: 'tuesday', dayName: 'Martes', isWorking: true },
        { day: 'wednesday', dayName: 'Miércoles', isWorking: true },
        { day: 'thursday', dayName: 'Jueves', isWorking: true },
        { day: 'friday', dayName: 'Viernes', isWorking: true },
        { day: 'saturday', dayName: 'Sábado', isWorking: false }
      ]);
    } finally {
      setLoadingSchedule(false);
    }

    // Cargar días bloqueados (filtrado por sede si aplica)
    try {
      const excLocParam = locationId ? `?locationId=${locationId}` : '';
      const excRes = await fetch(`${API_URL}/api/schedule/professional/${professionalId}/exceptions/public${excLocParam}`);
      if (excRes.ok) {
        const excData = await excRes.json();
        if (excData.success && excData.exceptions) {
          const blocked = excData.exceptions
            .filter((e: any) => !e.is_working)
            .map((e: any) => typeof e.exception_date === 'string' ? e.exception_date.split('T')[0] : new Date(e.exception_date).toISOString().split('T')[0]);
          setBlockedDates(blocked);
          const special = excData.exceptions
            .filter((e: any) => e.is_working && e.exception_type === 'custom_hours')
            .map((e: any) => typeof e.exception_date === 'string' ? e.exception_date.split('T')[0] : new Date(e.exception_date).toISOString().split('T')[0]);
          setSpecialDayDates(special);
        }
      }
    } catch (excErr) { console.error('Error cargando excepciones:', excErr); }
  };

  // Función para cargar horarios específicos de una fecha
  // Días habilitados para el servicio elegido: el backend ya resuelve si el
  // servicio tiene horario propio (que puede abrir días fuera del horario
  // general del profesional) o si sigue el horario normal.
  useEffect(() => {
    if (!user?.id || !selectedService?.id) {
      setServiceAvailableDates(null);
      setMotivosDias({});
      return;
    }
    let cancelled = false;
    setServiceAvailableDates(null);
    setMotivosDias({});
    const today = new Date();
    const endDate = new Date();
    // Hasta donde se puede reservar. Eran 60 días fijos: con 90 o 180, los
    // días que pasaban de 60 salían "No disponible" (Vero, oct-2026).
    endDate.setDate(today.getDate() + maxAdvanceBooking);
    const toStr = (d: Date) => `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
    const locParam = formData.locationId ? `&locationId=${formData.locationId}` : '';
    // Si falla, queda null y usa el horario general
    pedirDiasDelServicio(API_URL, user.id, `?start=${toStr(today)}&end=${toStr(endDate)}&serviceId=${selectedService.id}${locParam}`)
      .then((r) => {
        if (cancelled || !r) return;
        setServiceAvailableDates(r.fechas);
        setMotivosDias(r.motivos);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, selectedService?.id, formData.locationId, maxAdvanceBooking]);

  const loadTimeSlotsForDate = async (professionalId: number, date: string) => {
    if (!date) {
      setAvailableTimeSlots([]);
      return;
    }
    
    try {
      setLoadingTimeSlots(true);
      console.log(`🕐 Frontend: Cargando slots para fecha ${date}...`);
      
      const locationParam = formData.locationId ? `&locationId=${formData.locationId}` : '';
      const response = await fetch(
        `${API_URL}/api/appointments/professional/${professionalId}/available-slots?date=${date}&serviceId=${selectedService?.id || ''}${locationParam}`
      );
    
      console.log(`📡 Frontend: Response status:`, response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log(`📦 Frontend: Response data:`, data);
        
        if (data.success && Array.isArray(data.availableSlots)) {
          const times = data.availableSlots
            .filter((slot: any) => slot.available === true)
            .map((slot: any) => slot.time);
          
          console.log(`⏰ Frontend: ${times.length} slots procesados:`, times);
          setAvailableTimeSlots(times);
          
          if (times.length === 0) {
            console.log(`⚠️ Frontend: No hay slots disponibles para ${date}`);
          }
        } else {
          console.log(`❌ Frontend: Respuesta inválida:`, data);
          setAvailableTimeSlots([]);
        }
      } else {
        const errorData = await response.text();
        console.error(`❌ Frontend: Error response:`, response.status, errorData);
        setAvailableTimeSlots([]);
      }
    } catch (error) {
      console.error('❌ Frontend: Error en loadTimeSlotsForDate:', error);
      setAvailableTimeSlots([]);
    } finally {
      console.log(`🏁 Frontend: Finalizando carga de slots`);
      setLoadingTimeSlots(false);
    }
  };

  // Cargar servicios reales desde la base de datos
  const loadServices = async (professionalId: number) => {
    try {
      console.log('🛠️ Cargando servicios reales...');
      const response = await apiService.getProfessionalServices(professionalId);
      
      // Solo servicios activos
      const activeServices = response.services.filter(service => service.is_active);
      setServices(activeServices);
      
      console.log(`✅ ${activeServices.length} servicios activos cargados`);
    } catch (error: any) {
      console.error('❌ Error cargando servicios:', error);
      setError('Error cargando servicios: ' + apiService.getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // Cargar horarios cuando cambia la fecha
  useEffect(() => {
    if (formData.appointmentDate && user?.id) {
      loadTimeSlotsForDate(user.id, formData.appointmentDate);
    } else {
      setAvailableTimeSlots([]);
    }
  }, [formData.appointmentDate, user?.id, selectedService?.id, formData.locationId]);

  // Actualizar servicios seleccionados cuando cambian serviceIds
  useEffect(() => {
    if (formData.serviceIds.length > 0) {
      const selected = services.filter(s => formData.serviceIds.includes(s.id));
      setSelectedServices(selected);
      setSelectedService(selected[0] || null);
    } else if (formData.serviceId) {
      const service = services.find(s => s.id === parseInt(formData.serviceId));
      setSelectedService(service || null);
      setSelectedServices(service ? [service] : []);
    } else {
      setSelectedService(null);
      setSelectedServices([]);
    }
  }, [formData.serviceId, formData.serviceIds, services]);

  const handleInputChange = (field: keyof AppointmentForm, value: string | boolean) => {
    console.log(`📝 handleInputChange: ${field} = ${value}`);
    const newFormData = { ...formData, [field]: value };
    console.log(`📝 Nuevo formData:`, newFormData);
    setFormData(newFormData);
  };

  // 🆕 Buscar pacientes anteriores del profesional mientras se escribe el nombre
  useEffect(() => {
    if (skipPatientSearchRef.current) { skipPatientSearchRef.current = false; return; }
    const name = formData.clientName.trim();
    if (!user?.id || name.length < 2) {
      setPatientSuggestions([]); setShowPatientSuggestions(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(
          `${API_URL}/api/appointments/professional/${user.id}/patients?q=${encodeURIComponent(name)}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const data = await res.json();
        if (data.success && Array.isArray(data.patients)) {
          setPatientSuggestions(data.patients);
          setShowPatientSuggestions(data.patients.length > 0);
        }
      } catch { /* búsqueda silenciosa */ }
    }, 300);
    return () => clearTimeout(t);
  }, [formData.clientName, user]);

  // 🆕 Al elegir un paciente anterior, precargar sus datos
  const selectPatient = (p: any) => {
    skipPatientSearchRef.current = true;
    setFormData(prev => ({
      ...prev,
      clientName: p.clientName || prev.clientName,
      clientPhone: p.clientPhone || prev.clientPhone,
      clientEmail: p.clientEmail || prev.clientEmail
    }));
    if (p.patientExtraData && bookingExtraFields?.fields) {
      const allowed: Record<string, any> = {};
      bookingExtraFields.fields.forEach((f: any) => {
        if (p.patientExtraData[f.key] != null && p.patientExtraData[f.key] !== '') {
          allowed[f.key] = p.patientExtraData[f.key];
        }
      });
      if (Object.keys(allowed).length > 0) setPatientExtraData(prev => ({ ...prev, ...allowed }));
    }
    setShowPatientSuggestions(false);
    setPatientSuggestions([]);
  };

  const validateForm = (): string | null => {
    if (formData.serviceIds.length === 0 && !formData.serviceId) return 'Debe seleccionar un servicio';
    if (!formData.clientName.trim()) return 'El nombre del cliente es requerido';
    if (!formData.appointmentDate) return 'Debe seleccionar una fecha';
    if (!formData.appointmentTime) return 'Debe seleccionar una hora';

    // Validar email solo si fue ingresado
    if (formData.clientEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.clientEmail)) {
        return 'El email no tiene un formato válido';
      }
    }

    // Validar fecha y hora
    const appointmentDateTime = new Date(`${formData.appointmentDate} ${formData.appointmentTime}`);
    const now = new Date();
    
    const isToday = new Date(formData.appointmentDate).toDateString() === now.toDateString();
    if (isToday && appointmentDateTime <= now) {
      return 'Para citas el día de hoy, la hora debe ser futura';
    }
    
    if (appointmentDateTime < now && !isToday) {
      return 'No se pueden agendar citas en fechas pasadas';
    }

    // Validar que el slot seleccionado esté disponible (excepto sobreturnos)
    const isSlotAvailable = availableTimeSlots.includes(formData.appointmentTime);

    if (!isSlotAvailable && !isOverride) {
      return 'El horario seleccionado ya no está disponible';
    }

    return null;
  };

  // Función para formatear fecha correctamente (arregla problema de zona horaria)
  const formatDateForDisplay = (dateString: string) => {
    try {
      // Crear fecha localmente para evitar problemas de zona horaria
      const [year, month, day] = dateString.split('-');
      const localDate = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day)
      );
      
      return localDate.toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return dateString;
    }
  };

  // Función para cerrar modal y redirigir
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setAppointmentCreated(null);
  };

  // Guardar cita real en la base de datos
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);
    
    try {
      console.log('💾 Creando cita real en la base de datos...');

      // 🆕 Si se adjuntó comprobante, lo subimos primero. El backend ya guarda
      // comprobante_pago_url + pago_verificado (auto-verificado).
      let comprobantePagoUrl: string | null = null;
      if (comprobanteFile) {
        try {
          setUploadingComprobante(true);
          const fd = new FormData();
          fd.append('proof', comprobanteFile);
          const upRes = await fetch(`${API_URL}/api/payments/verification/upload-proof`, {
            method: 'POST',
            body: fd
          });
          const upData = await upRes.json();
          if (upData.success && upData.proofUrl) {
            comprobantePagoUrl = upData.proofUrl;
          } else {
            setError('No se pudo subir el comprobante. Probá de nuevo o creá la cita sin él.');
            setSaving(false);
            setUploadingComprobante(false);
            return;
          }
        } finally {
          setUploadingComprobante(false);
        }
      }

      const appointmentData: any = {
        serviceId: formData.serviceIds.length > 0 ? formData.serviceIds[0] : parseInt(formData.serviceId),
        service_ids: formData.serviceIds.length > 1 ? formData.serviceIds : undefined,
        clientName: formData.clientName,
        clientEmail: formData.clientEmail,
        clientPhone: formData.clientPhone || undefined,
        appointmentDate: formData.appointmentDate,
        appointmentTime: formData.appointmentTime,
        clientNotes: formData.notes || undefined,
        isFirstTime: formData.isFirstTime,
        urgencyLevel: formData.urgencyLevel,
        locationId: formData.locationId ? parseInt(formData.locationId) : undefined,
        ...(isOverride && { isOverride: true }),
        // 🆕 Comprobante adjunto → cita marcada como pago verificado (transferencia)
        ...(comprobantePagoUrl && {
          comprobante_pago_url: comprobantePagoUrl,
          pago_verificado: true,
          metodo_pago_elegido: 'transferencia'
        }),
        ...(bookingExtraFields && Object.keys(patientExtraData).some(k => patientExtraData[k]) && {
          patient_extra_data: Object.fromEntries(Object.entries(patientExtraData).filter(([_, v]) => v))
        })
      };

      const response = await apiService.createAppointment(user.id, appointmentData);
      
      console.log('✅ Cita creada exitosamente:', response.appointment);
      
      // 🎉 MOSTRAR MODAL EN LUGAR DE ALERT
      setAppointmentCreated({
        ...response.appointment,
        serviceName: selectedServices.map(s => s.name).join(' + ') || selectedService?.name,
        servicePrice: selectedServices.reduce((sum, s) => sum + (parseFloat(String(s.price)) || 0), 0) || selectedService?.price,
        clientName: formData.clientName,
        originalAppointmentDate: formData.appointmentDate, // Guardar la fecha original
        formattedDate: formatDateForDisplay(formData.appointmentDate), // Usar la función corregida
        appointmentTime: formData.appointmentTime
      });
      setShowSuccessModal(true);
      
      // Limpiar formulario
      setFormData({
        serviceId: '',
        serviceIds: [],
        locationId: '',
        clientName: '',
        clientEmail: '',
        clientPhone: '',
        appointmentDate: '',
        appointmentTime: '',
        notes: '',
        isFirstTime: false,
        urgencyLevel: 'normal'
      });
      setSelectedService(null);
      setSelectedServices([]);
      setAvailableTimeSlots([]);
      setPatientExtraData({});
      setComprobanteFile(null);

    } catch (error: any) {
      console.error('❌ Error creando cita:', error);
      setError('Error al crear la cita: ' + apiService.getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const getMinDate = () => {
    const today = new Date();
    return `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;
  };

  const getDepositAmount = () => {
    if (!selectedService || !selectedService.requires_deposit) return 0;
    
    if (selectedService.deposit_amount) {
      return selectedService.deposit_amount;
    }
    
    if (selectedService.deposit_percentage) {
      return (selectedService.price * selectedService.deposit_percentage) / 100;
    }
    
    return 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl animate-pulse mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando servicios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header Premium */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                {user?.professional_type === 'belleza' ? <Sparkles className="w-5 h-5 text-white" /> : <Stethoscope className="w-5 h-5 text-white" />}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Nueva Cita</h1>
                <p className="text-white/70 text-sm mt-0.5">Programa una nueva cita para tu cliente</p>
              </div>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-xl transition-colors font-medium"
            >
              ← Volver
            </button>
          </div>
        </div>
      </div>

      {/* Mostrar errores */}
      {error && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center">
              <XCircle className="w-6 h-6 text-red-500 mr-2 flex-shrink-0" />
              <span className="text-red-700">{error}</span>
              <button 
                onClick={() => setError(null)}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Banner de sobreturno */}
        {isOverride && (
          <div className="mb-6 bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-bold text-orange-800">Sobreturno</h3>
              <p className="text-sm text-orange-700">
                Estás creando un turno adicional para el {preloadDate.split('-').reverse().join('/')} a las {preloadTime}.
                Este turno se añadirá aunque el horario ya esté ocupado.
              </p>
            </div>
          </div>
        )}
        {/* Banner de turno pre-cargado (no sobreturno) */}
        {preloadDate && preloadTime && !isOverride && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <CalendarDays className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-blue-800">Nuevo turno</h3>
              <p className="text-sm text-blue-700">
                Fecha y hora pre-seleccionadas: {preloadDate.split('-').reverse().join('/')} a las {preloadTime}
              </p>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Selector de Sede (solo si hay más de una) */}
          {locations.length > 1 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-6 h-6 bg-teal-500 rounded-lg flex items-center justify-center">
                  <MapPin className="w-3.5 h-3.5 text-white" />
                </span>
                <h4 className="font-bold text-gray-900 text-sm">Seleccionar Sede</h4>
              </div>
              {loadingLocations ? (
                <div className="flex items-center gap-2 text-gray-500 py-3">
                  <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  Cargando sedes...
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {locations.map(loc => (
                    <button
                      key={loc.id}
                      type="button"
                      onClick={() => {
                        // Si viene con fecha/hora precargada (sobreturno), no borrarlas
                        if (preloadDate && preloadTime) {
                          setFormData(prev => ({ ...prev, locationId: String(loc.id) }));
                        } else {
                          setFormData(prev => ({ ...prev, locationId: String(loc.id), appointmentDate: '', appointmentTime: '' }));
                        }
                        setProfessionalSchedule([]);
                        setBlockedDates([]);
                        setAvailableTimeSlots([]);
                        if (user?.id) loadProfessionalSchedule(user.id, loc.id);
                      }}
                      className={`p-4 rounded-2xl border-2 text-left transition-all duration-200 ${
                        formData.locationId === String(loc.id)
                          ? 'border-teal-500 bg-teal-50 shadow-md'
                          : 'border-gray-200 bg-white hover:border-teal-300 hover:bg-teal-50/50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          formData.locationId === String(loc.id) ? 'border-teal-500' : 'border-gray-300'
                        }`}>
                          {formData.locationId === String(loc.id) && (
                            <div className="w-3 h-3 rounded-full bg-teal-500"></div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 text-sm">{loc.name}</p>
                          {loc.city && <p className="text-xs text-gray-500 mt-0.5">{loc.city}</p>}
                          {loc.address && <p className="text-xs text-gray-400">{loc.address}</p>}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Información del servicio */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-6 h-6 bg-blue-500 rounded-lg flex items-center justify-center">
                <Target className="w-3.5 h-3.5 text-white" />
              </span>
              <h4 className="font-bold text-gray-900 text-sm">Seleccionar Servicio</h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                  Servicio *
                </label>
                
                {services.length === 0 ? (
                  <div className="w-full p-3 border border-red-300 rounded-lg bg-red-50">
                    <p className="text-red-700 text-sm flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" /> No tienes servicios configurados.
                      <button
                        type="button"
                        onClick={() => navigate('/professional/services-config')}
                        className="ml-2 text-red-800 underline hover:text-red-900"
                      >
                        Configura tus servicios aquí
                      </button>
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1.5 max-h-48 overflow-y-auto border border-gray-100 rounded-2xl p-2">
                    {services.map(service => {
                      const isChecked = formData.serviceIds.includes(service.id);
                      return (
                        <label key={service.id} className={`flex items-center gap-2 p-3 rounded-xl cursor-pointer hover:bg-blue-50/50 text-sm transition-colors ${isChecked ? 'bg-blue-50 border border-blue-200' : ''}`}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              const newIds = isChecked
                                ? formData.serviceIds.filter(id => id !== service.id)
                                : [...formData.serviceIds, service.id];
                              setFormData(prev => ({
                                ...prev,
                                serviceIds: newIds,
                                serviceId: newIds[0]?.toString() || ''
                              }));
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="flex-1 font-medium text-gray-900">{service.name}</span>
                          <span className="text-gray-400 text-xs">{service.duration}min</span>
                          <span className="text-gray-400 text-xs">{service.category}</span>
                          <span className="text-gray-700 font-semibold">{displayPrice(service as any)}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {formData.serviceIds.length > 1 && (
                  <div className="mt-2 p-3 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                    {formData.serviceIds.length} servicios seleccionados | Duración total: {selectedServices.reduce((sum, s) => sum + s.duration, 0)} min | Total: ${selectedServices.reduce((sum, s) => sum + (parseFloat(String(s.price)) || 0), 0)}
                  </div>
                )}
              </div>

              {selectedServices.length > 0 && (
                <div className="md:col-span-2 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-4">
                  <h3 className="font-bold text-gray-900 text-sm mb-2">{selectedServices.map(s => s.name).join(' + ')}</h3>
                  {selectedServices.length === 1 && selectedServices[0].description && (
                    <p className="text-gray-600 text-sm mb-3">{selectedServices[0].description}</p>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Duración:</span>
                      <p className="text-gray-600">{selectedServices.reduce((sum, s) => sum + s.duration, 0)} min</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Precio:</span>
                      <p className="text-gray-600">${selectedServices.reduce((sum, s) => sum + (parseFloat(String(s.price)) || 0), 0)}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Categoría:</span>
                      <p className="text-gray-600">{Array.from(new Set(selectedServices.map(s => s.category))).join(', ')}</p>
                    </div>
                    {selectedService?.requires_deposit && (
                      <div>
                        <span className="font-medium text-gray-700">Seña:</span>
                        <p className="text-gray-600">${getDepositAmount()}</p>
                      </div>
                    )}
                  </div>

                  {selectedService?.requires_consultation && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="text-yellow-800 text-sm flex items-start gap-2">
                        <MessageCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> Este servicio requiere consulta previa. Se recomienda contactar al cliente antes de la cita.
                      </p>
                    </div>
                  )}

                  {(selectedService?.before_care_instructions || selectedService?.after_care_instructions) && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                      {selectedService?.before_care_instructions && (
                        <p className="text-blue-800 text-sm mb-2">
                          <span className="font-medium">Antes:</span> {selectedService.before_care_instructions}
                        </p>
                      )}
                      {selectedService?.after_care_instructions && (
                        <p className="text-blue-800 text-sm">
                          <span className="font-medium">Después:</span> {selectedService.after_care_instructions}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Información del cliente */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-6 h-6 bg-emerald-500 rounded-lg flex items-center justify-center">
                <User className="w-3.5 h-3.5 text-white" />
              </span>
              <h4 className="font-bold text-gray-900 text-sm">Informacion del Cliente</h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                  Nombre completo *
                </label>
                <input
                  type="text"
                  name="patient-name-search"
                  autoComplete="off" autoCorrect="off" spellCheck={false}
                  value={formData.clientName}
                  onChange={(e) => handleInputChange('clientName', e.target.value)}
                  onFocus={() => { if (patientSuggestions.length > 0) setShowPatientSuggestions(true); }}
                  onBlur={() => setTimeout(() => setShowPatientSuggestions(false), 150)}
                  required
                  className="w-full input-premium"
                  placeholder="Nombre y apellido del cliente"
                />
                {showPatientSuggestions && patientSuggestions.length > 0 && (
                  <div className="absolute z-30 left-0 right-0 mt-2 bg-white border border-gray-100 rounded-xl shadow-xl ring-1 ring-black/5 overflow-hidden">
                    <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 bg-gray-50/80 border-b border-gray-100 flex items-center gap-1.5">
                      <User className="w-3 h-3" /> Pacientes anteriores
                    </div>
                    <div className="max-h-60 overflow-y-auto py-1">
                      {patientSuggestions.map((p, i) => {
                        const initials = (p.clientName || '?').trim().split(/\s+/).slice(0, 2).map((w: string) => w[0]).join('').toUpperCase();
                        return (
                          <button
                            key={i}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); selectPatient(p); }}
                            className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-emerald-50 transition-colors group"
                          >
                            <span className="w-8 h-8 flex-shrink-0 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-white text-xs font-bold flex items-center justify-center shadow-sm">
                              {initials || '?'}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block font-medium text-gray-800 text-sm truncate group-hover:text-emerald-700">{p.clientName}</span>
                              <span className="block text-xs text-gray-500 truncate">
                                {p.clientPhone || 'sin teléfono'}{p.clientEmail ? ` • ${p.clientEmail}` : ''}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                  Email <span className="text-gray-300 normal-case">(opcional)</span>
                </label>
                <input
                  type="email"
                  value={formData.clientEmail}
                  onChange={(e) => handleInputChange('clientEmail', e.target.value)}
                  className="w-full input-premium"
                  placeholder="Si no tiene, dejar vacío"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  required
                  value={formData.clientPhone}
                  onChange={(e) => handleInputChange('clientPhone', e.target.value)}
                  className="w-full input-premium"
                  placeholder="+54 11 1234-5678"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                  Nivel de urgencia
                </label>
                <select
                  value={formData.urgencyLevel}
                  onChange={(e) => handleInputChange('urgencyLevel', e.target.value as 'low' | 'normal' | 'high')}
                  className="w-full input-premium"
                >
                  <option value="low">Baja</option>
                  <option value="normal">Normal</option>
                  <option value="high">Alta</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isFirstTime}
                    onChange={(e) => handleInputChange('isFirstTime', e.target.checked)}
                    className="mr-2 h-4 w-4 text-blue-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Es la primera vez de este cliente</span>
                </label>
              </div>

              {/* 🆕 Comprobante de pago (opcional). Si se sube, la cita queda con el pago verificado. */}
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                  <FileText className="w-3.5 h-3.5 inline mr-1" /> Comprobante de pago <span className="text-gray-300 normal-case">(opcional)</span>
                </label>
                {comprobanteFile ? (
                  <div className="flex items-center justify-between gap-2 px-3 py-2 border border-emerald-200 bg-emerald-50 rounded-lg text-sm">
                    <span className="truncate text-emerald-700 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 flex-shrink-0" /> {comprobanteFile.name}
                    </span>
                    <button type="button" onClick={() => setComprobanteFile(null)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => setComprobanteFile(e.target.files?.[0] || null)}
                    className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                  />
                )}
                <p className="text-xs text-gray-400 mt-0.5">Si lo cargás, la cita queda con el pago verificado.</p>
              </div>
            </div>
          </div>

          {/* Campos dinámicos extra del profesional */}
          {bookingExtraFields && bookingExtraFields.fields.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-6 h-6 bg-violet-500 rounded-lg flex items-center justify-center">
                  <ClipboardList className="w-3.5 h-3.5 text-white" />
                </span>
                <h4 className="font-bold text-gray-900 text-sm">Datos Adicionales del Paciente</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {bookingExtraFields.fields.map((field: any) => (
                  <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                    <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    {field.type === 'textarea' ? (
                      <textarea
                        value={patientExtraData[field.key] || ''}
                        onChange={(e) => setPatientExtraData(prev => ({ ...prev, [field.key]: e.target.value }))}
                        rows={3}
                        required={field.required}
                        className="w-full input-premium text-sm"
                        placeholder={field.label}
                      />
                    ) : field.type === 'select' && field.options ? (
                      <select
                        value={patientExtraData[field.key] || ''}
                        onChange={(e) => setPatientExtraData(prev => ({ ...prev, [field.key]: e.target.value }))}
                        required={field.required}
                        className="w-full input-premium text-sm"
                      >
                        <option value="">Seleccionar...</option>
                        {field.options.map((opt: string) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type === 'date' ? 'date' : 'text'}
                        value={patientExtraData[field.key] || ''}
                        onChange={(e) => setPatientExtraData(prev => ({ ...prev, [field.key]: e.target.value }))}
                        required={field.required}
                        className="w-full input-premium text-sm"
                        placeholder={field.label}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fecha y hora con CustomCalendar (requiere sede si hay múltiples) */}
          {/* Si viene con fecha+hora precargada (sobreturno o desde calendario), mostrar resumen en vez del selector */}
          {preloadDate && preloadTime && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-gray-600" /> Fecha y Hora
              </h2>
              <div className={`p-4 rounded-lg border ${isOverride ? 'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200'}`}>
                <p className={`text-sm font-medium flex items-center gap-2 ${isOverride ? 'text-orange-800' : 'text-blue-800'}`}>
                  {isOverride ? <><Zap className="w-4 h-4" /> Sobreturno: </> : <><CalendarDays className="w-4 h-4" /> </>}
                  {formatDateForDisplay(preloadDate)} a las <strong>{preloadTime} hs</strong>
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/professional/new-appointment')}
                className="mt-2 text-xs text-gray-500 hover:text-gray-700 underline"
              >
                Cambiar fecha y hora
              </button>
            </div>
          )}
          {!preloadDate && !preloadTime && (locations.length <= 1 || formData.locationId) && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-6 h-6 bg-amber-500 rounded-lg flex items-center justify-center">
                <CalendarDays className="w-3.5 h-3.5 text-white" />
              </span>
              <h4 className="font-bold text-gray-900 text-sm">Fecha y Hora</h4>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Calendario personalizado */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selecciona una fecha *
                </label>
                
                {loadingSchedule ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mr-3"></div>
                    <span className="text-gray-600">Cargando calendario...</span>
                    </div>
                ) : (
                  <CustomCalendar
                    selectedDate={formData.appointmentDate}
                    onDateSelect={(date) => {
                      console.log('📅 DEBUG: Fecha seleccionada:', date);
                      console.log('🎯 DEBUG: selectedService:', selectedService);
                      console.log('🎯 DEBUG: formData.serviceId:', formData.serviceId);
                      console.log('👤 DEBUG: user?.id:', user?.id);
                      
                      // FORZAR la actualización del estado
                      setFormData(prev => {
                        const updated = { ...prev, appointmentDate: date, appointmentTime: '' };
                        console.log('📝 ACTUALIZANDO formData directamente:', updated);
                        return updated;
                      });
                      
                      if (user?.id) {
                        loadTimeSlotsForDate(user.id, date);
                      }
                    }}
                    professionalSchedule={professionalSchedule}
                    blockedDates={blockedDates}
                    specialDayDates={specialDayDates}
                    onlySpecialDays={onlySpecialDays}
                    serviceAvailableDates={serviceAvailableDates}
                    motivos={motivosDias}
                    minDate={getMinDate()}
                    maxDate={(() => {
                      const d = new Date();
                      d.setDate(d.getDate() + maxAdvanceBooking);
                      return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
                    })()}
                  />
                )}
                
                {formData.appointmentDate && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800 font-medium flex items-center gap-2">
                      <CalendarDays className="w-4 h-4" /> Fecha seleccionada: {formatDateForDisplay(formData.appointmentDate)}
                    </p>
                  </div>
                )}
              </div>

              {/* REEMPLAZAR la sección de horarios disponibles */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Horarios disponibles * 
                  {loadingTimeSlots && <span className="text-blue-500 ml-2">(Cargando...)</span>}
                  {!loadingTimeSlots && formData.appointmentDate && availableTimeSlots.length === 0 && (
                    <span className="text-amber-600 ml-2">(No hay horarios disponibles)</span>
                  )}
                </label>
                
                {formData.appointmentDate ? (
                  loadingTimeSlots ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full mr-2"></div>
                      <span className="text-sm text-gray-600">Cargando horarios...</span>
                    </div>
                  ) : availableTimeSlots.length > 0 ? (
                    <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                      {availableTimeSlots.map((time) => (
                        <button
                          key={time}
                          type="button"
                          onClick={() => handleInputChange('appointmentTime', time)}
                          className={`time-slot text-sm ${
                            formData.appointmentTime === time
                              ? 'time-slot-selected'
                              : ''
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
                      <svg 
                        className="w-12 h-12 text-amber-400 mx-auto mb-3" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={1.5} 
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
                        />
                      </svg>
                      <p className="text-amber-700 font-medium">No hay horarios disponibles</p>
                      <p className="text-sm text-amber-600 mt-2">
                        El profesional no tiene disponibilidad para esta fecha.
                      </p>
                      <p className="text-xs text-amber-500 mt-2">
                        Fecha: {formData.appointmentDate} | 
                        Servicio: {selectedServices.map(s => s.name).join(' + ') || 'No seleccionado'}
                      </p>
                    </div>
                  )
                ) : (
                  <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-gray-600">Selecciona una fecha para ver los horarios disponibles</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          )}

          {/* Notas adicionales */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-6 h-6 bg-slate-500 rounded-lg flex items-center justify-center">
                <FileText className="w-3.5 h-3.5 text-white" />
              </span>
              <h4 className="font-bold text-gray-900 text-sm">Notas Adicionales</h4>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observaciones o notas especiales
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={4}
                className="w-full input-premium"
                placeholder="Detalles adicionales, preferencias del cliente, alergias conocidas, etc."
              />
            </div>
          </div>

          {/* Resumen y confirmación */}
          {selectedServices.length > 0 && formData.appointmentDate && formData.appointmentTime && (
            <div className="bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50 border border-indigo-100 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5">
                <span className="w-7 h-7 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center shadow-sm">
                  <ClipboardList className="w-4 h-4 text-white" />
                </span>
                <h2 className="text-base font-bold text-gray-800">Resumen de la Cita</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white/60 rounded-xl p-4 border border-white">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Servicio</h3>
                  <div className="space-y-2.5 text-sm">
                    <p className="text-gray-700"><span className="font-semibold text-gray-900">{selectedServices.map(s => s.name).join(' + ')}</span></p>
                    <p className="text-gray-600"><span className="font-medium">Duración:</span> {selectedServices.reduce((sum, s) => sum + s.duration, 0)} min</p>
                    <p className="text-gray-600"><span className="font-medium">Precio:</span> <span className="font-bold text-indigo-600">${selectedServices.reduce((sum, s) => sum + (parseFloat(String(s.price)) || 0), 0)}</span></p>
                    {selectedService?.requires_deposit && (
                      <p className="text-gray-600"><span className="font-medium">Seña:</span> <span className="font-semibold text-amber-600">${getDepositAmount()}</span></p>
                    )}
                  </div>
                </div>

                <div className="bg-white/60 rounded-xl p-4 border border-white">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Detalles</h3>
                  <div className="space-y-2.5 text-sm">
                    <p className="text-gray-600"><span className="font-medium">Fecha:</span> <span className="font-semibold text-gray-900">{formatDateForDisplay(formData.appointmentDate)}</span></p>
                    <p className="text-gray-600"><span className="font-medium">Hora:</span> <span className="font-semibold text-indigo-600">{formData.appointmentTime}</span></p>
                    <p className="text-gray-600"><span className="font-medium">Cliente:</span> {formData.clientName}</p>
                    <p className="text-gray-600"><span className="font-medium">Email:</span> {formData.clientEmail}</p>
                    {formData.clientPhone && (
                      <p className="text-gray-600"><span className="font-medium">Tel:</span> {formData.clientPhone}</p>
                    )}
                    {formData.locationId && locations.length > 0 && (
                      <p className="text-gray-600 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {locations.find(l => l.id === parseInt(formData.locationId))?.name || ''}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Botones de acción */}
          <div className="flex justify-end space-x-4 pt-6">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-3 glass-card text-gray-600 rounded-2xl hover:bg-gray-50 transition-all duration-200 font-medium"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={saving}
              className="px-8 py-3 btn-premium font-semibold text-base"
            >
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  {uploadingComprobante ? 'Subiendo comprobante...' : 'Creando cita...'}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2"><CheckCircle className="w-5 h-5" /> Crear Cita</span>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Modal de Éxito con Animación - FECHA CORREGIDA */}
      {showSuccessModal && appointmentCreated && createPortal(
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end md:items-center justify-center z-[9999] md:px-4">
          <div className="bg-white rounded-t-3xl md:rounded-3xl max-w-md w-full overflow-hidden transform transition-all duration-300 shadow-2xl">
            {/* Gradient header strip */}
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 pt-8 pb-6 text-center">
              <div className="mx-auto w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              <h3 className="text-2xl font-bold text-white mb-1">
                ¡Cita Agendada!
              </h3>

              <p className="text-white/80 text-sm">
                La cita se ha programado exitosamente
              </p>
            </div>

            {/* Detalles de la cita */}
            <div className="px-8 py-6">
              <div className="bg-gray-50/80 rounded-2xl p-5 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Cliente</span>
                  <span className="text-sm text-gray-900 font-semibold">
                    {appointmentCreated.clientName}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Servicio</span>
                  <span className="text-sm text-gray-900 font-semibold">
                    {appointmentCreated.serviceName}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Fecha</span>
                  <span className="text-sm text-gray-900 font-semibold capitalize">
                    {formatDateForDisplay(appointmentCreated.originalAppointmentDate || formData.appointmentDate)}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Hora</span>
                  <span className="text-sm text-indigo-600 font-bold">
                    {appointmentCreated.appointmentTime}
                  </span>
                </div>

                {formData.locationId && locations.length > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Sede</span>
                    <span className="text-sm text-gray-900 font-semibold flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-gray-400" /> {locations.find(l => l.id === parseInt(formData.locationId))?.name || ''}
                    </span>
                  </div>
                )}

                <div className="flex justify-between items-center border-t border-gray-200/60 pt-3 mt-3">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Precio</span>
                  <span className="text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    ${appointmentCreated.servicePrice}
                  </span>
                </div>
              </div>

              {/* Botón de acción */}
              <button
                onClick={handleSuccessModalClose}
                className="w-full btn-premium font-semibold py-3.5 px-6 text-base mt-5"
              >
                Entendido
              </button>

              {/* Mensaje adicional */}
              <p className="text-center text-xs text-gray-400 mt-4">
                Se ha enviado una confirmación por email al cliente
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NewAppointmentForm;